import { useState } from "react";
import {
  api,
  ApiError,
  type AgentConfigBody,
  type ExpertSummary,
  type QaItem,
  type Language,
} from "../api";
import { ExpertMark } from "./design/ExpertMark";
import { Icon, type IconName } from "./design/Icon";
import { VoiceSignature } from "./design/VoiceSignature";

export type ExpertTone = "consultant" | "advisor" | "reviewer";
export type MeetingPhase = "in_meeting" | "meeting_over" | "finalized";

const TONE_OPTIONS: Array<{ value: ExpertTone; label: string }> = [
  { value: "consultant", label: "Consultant" },
  { value: "advisor", label: "Advisor" },
  { value: "reviewer", label: "Reviewer" },
];

/** A single expert's "desk" — matches the design's `ExpertDesk` in
 *  `add-production-shell-screens/design-handoff/project/screens-meeting.jsx`.
 *  Colored gradient header band, large ExpertMark, Instrument Serif name
 *  + `[short]` mono annotation, color-tinted compact tone toggle, language
 *  + voiceDesc dim row, voice signature waveform on the right (pulsing
 *  while speaking), and a status row of 4 DeskStats. */
export function Desk({
  meetingId,
  expert,
  tone,
  phase,
  qaItems,
  language,
  agent,
  archetypeDisplayName,
  speaking,
  onToneChange,
}: {
  meetingId: string;
  expert: ExpertSummary;
  tone: ExpertTone;
  phase: MeetingPhase;
  /** Q&A items for this expert only. */
  qaItems: QaItem[];
  /** Meeting language — surfaces in the desk's identity row. */
  language: Language;
  /** Resolved agent config — surfaces the archetype name + three
   *  read-only axis chips in the identity band when present. Optional
   *  for back-compat with older callers that don't yet thread it. */
  agent?: AgentConfigBody;
  /** Archetype display name lookup — the api returns axis values on
   *  `agent`; the human-readable name lives on `ExpertSummary.archetypes`.
   *  When the lookup fails (e.g. registry not loaded), the chip falls
   *  back to the archetype id verbatim. */
  archetypeDisplayName?: string;
  /** When provided, overrides the internal speaking signal — used by
   *  the parent to rotate the active speaker across Desks every few
   *  seconds when voice is on. */
  speaking?: boolean;
  onToneChange: (next: ExpertTone) => void;
}) {
  const [pendingTone, setPendingTone] = useState<ExpertTone | null>(null);
  const c = expert.color_token;
  const live = phase === "in_meeting";
  const isSpeaking = (speaking ?? false) && live;

  async function changeTone(next: ExpertTone) {
    if (next === tone) return;
    if (phase === "finalized") return;
    const snapshot = tone;
    setPendingTone(next);
    onToneChange(next);
    try {
      await api.updateExpertTones(meetingId, { [expert.id]: next });
    } catch (e) {
      onToneChange(snapshot);
      console.error(
        "tone PATCH failed:",
        e instanceof ApiError ? e.message : String(e),
      );
    } finally {
      setPendingTone(null);
    }
  }

  const postedCount = qaItems.filter(
    (q) => q.status === "posted",
  ).length;
  const spokenCount = qaItems.filter((q) => q.status === "spoken").length;
  // "awaiting-cue" status retired by introduce-approval-queue-concept;
  // the Highlights stat now reflects the count of items currently
  // pending the user's thumbs-up review.
  const highlightsCount = qaItems.filter(
    (q) => q.status === "pending",
  ).length;

  return (
    <article
      style={{
        borderRadius: 12,
        background: "var(--card)",
        border: `1px solid color-mix(in oklab, var(--${c}) 32%, var(--rule))`,
        boxShadow: isSpeaking
          ? `0 0 0 3px color-mix(in oklab, var(--${c}) 22%, transparent), var(--sh-2)`
          : "var(--sh-1)",
        transition: "box-shadow .25s",
        overflow: "hidden",
      }}
    >
      {/* Identity band — colored gradient, monogram + name + tone +
          voice signature. The visual anchor of the whole component. */}
      <div
        style={{
          background: `linear-gradient(180deg, var(--${c}-tint), color-mix(in oklab, var(--${c}-tint) 70%, var(--card)))`,
          padding: "14px 16px",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          borderBottom: `1px solid color-mix(in oklab, var(--${c}) 25%, transparent)`,
        }}
      >
        <ExpertMark expert={expert} size={42} ring={isSpeaking} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              className="serif"
              style={{
                fontSize: 20,
                fontWeight: 400,
                lineHeight: 1.1,
                color: `var(--${c}-deep)`,
              }}
            >
              {expert.display_name}
            </span>
            <span
              className="mono"
              style={{ fontSize: 11, color: `var(--${c}-deep)`, opacity: 0.8 }}
            >
              [{expert.short_name}]
            </span>
          </div>
          <div
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              className="mono"
              style={{
                fontSize: 9.5,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: `var(--${c}-deep)`,
                opacity: 0.7,
              }}
            >
              Tone
            </span>
            <ToneControl
              value={tone}
              colorToken={c}
              disabled={!live || pendingTone !== null}
              onChange={changeTone}
            />
          </div>
          {agent && (
            <div
              style={{
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 10,
                  padding: "1px 6px",
                  borderRadius: 4,
                  background: `color-mix(in oklab, var(--${c}) 12%, var(--card))`,
                  border: `0.5px solid color-mix(in oklab, var(--${c}) 35%, transparent)`,
                  color: `var(--${c}-deep)`,
                }}
                title="Archetype — switchable from the Tweaks panel"
              >
                {archetypeDisplayName ?? agent.archetype_id}
              </span>
              <AxisChip label={agent.axes.assertiveness} color={c} />
              <AxisChip label={agent.axes.responsiveness} color={c} />
              <AxisChip label={agent.axes.evidence} color={c} />
            </div>
          )}
          <div
            className="dim"
            style={{
              fontSize: 11,
              marginTop: 6,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {language} · {expert.voice_desc}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 4,
            flexShrink: 0,
            paddingTop: 4,
          }}
        >
          <VoiceSignature
            expert={expert}
            active={isSpeaking}
            height={16}
            bars={14}
          />
          {isSpeaking && (
            <span
              className="mono"
              style={{
                fontSize: 9.5,
                color: `var(--${c}-deep)`,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              speaking
            </span>
          )}
        </div>
      </div>

      {/* Status row — four DeskStats, each Icon + label + value. Matches
          the design's "Listening · Posted · Spoken · Highlights" strip. */}
      <div
        style={{
          padding: "8px 16px",
          display: "flex",
          gap: 16,
          alignItems: "center",
          fontSize: 11.5,
          flexWrap: "wrap",
        }}
      >
        <DeskStat
          icon="eye"
          label="Listening"
          value={live ? "active" : "—"}
          pulse={live}
        />
        <DeskStat icon="chat" label="Posted" value={String(postedCount)} />
        <DeskStat
          icon="speaker"
          label="Spoken"
          value={isSpeaking ? "now" : String(spokenCount)}
        />
        <DeskStat
          icon="spark"
          label="Highlights"
          value={String(highlightsCount)}
        />
      </div>
    </article>
  );
}

function ToneControl({
  value,
  colorToken,
  disabled,
  onChange,
}: {
  value: ExpertTone;
  colorToken: string;
  disabled: boolean;
  onChange: (next: ExpertTone) => void;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        padding: 2,
        gap: 2,
        borderRadius: 7,
        background: `color-mix(in oklab, var(--${colorToken}) 10%, var(--card))`,
        border: `1px solid color-mix(in oklab, var(--${colorToken}) 28%, var(--rule))`,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {TONE_OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            disabled={disabled}
            aria-pressed={active}
            style={{
              padding: "3px 9px",
              fontSize: 11,
              lineHeight: 1.2,
              fontFamily: "inherit",
              cursor: disabled ? "not-allowed" : "pointer",
              borderRadius: 5,
              border: "none",
              background: active ? `var(--${colorToken})` : "transparent",
              color: active ? "var(--paper)" : `var(--${colorToken}-deep)`,
              fontWeight: active ? 600 : 500,
              letterSpacing: ".01em",
              transition: "background .15s, color .15s",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </span>
  );
}

function DeskStat({
  icon,
  label,
  value,
  pulse,
}: {
  icon: IconName;
  label: string;
  value: string;
  pulse?: boolean;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        color: "var(--ink-3)",
      }}
    >
      <Icon name={icon} size={12} />
      <span>{label}</span>
      <span style={{ color: "var(--ink)", fontWeight: 500 }}>{value}</span>
      {pulse && (
        <span
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "var(--ok)",
            boxShadow: "0 0 0 0 var(--ok)",
            animation: "desk-stat-pulse 1.6s ease-out infinite",
          }}
        >
          <style>{`
            @keyframes desk-stat-pulse {
              0%   { box-shadow: 0 0 0 0 currentColor; }
              70%  { box-shadow: 0 0 0 6px rgba(0,0,0,0); }
              100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
            }
          `}</style>
        </span>
      )}
    </span>
  );
}

function AxisChip({ label, color }: { label: string; color: string }) {
  return (
    <span
      className="mono"
      style={{
        fontSize: 9.5,
        padding: "1px 5px",
        borderRadius: 3,
        background: `color-mix(in oklab, var(--${color}) 6%, var(--card))`,
        border: `0.5px solid var(--rule-2)`,
        color: "var(--ink-3)",
        textTransform: "lowercase",
        letterSpacing: ".02em",
      }}
    >
      {label}
    </span>
  );
}
